import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card"
import { useMediaQuery } from "@uidotdev/usehooks"
import { ChevronLeft, ChevronRight, Info } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useWeeklyObjectivesStore } from "../../stores/use-weekly-objectives-store"
import { motion } from "framer-motion"
import type { Course } from "@/modules/courses/entities/courses"
import { msToSeconds } from "../../utils/time"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import {
  isWithinInterval,
  toDate,
  isBefore,
  startOfDay,
  parseISO,
  differenceInSeconds,
  startOfWeek,
  endOfWeek,
  addWeeks,
} from "date-fns"
import useGetCalendar from "@/modules/calendar/queries/use-get-calendar"
import extractISODate from "@/modules/calendar/utils/extract-iso-date"
import type { UserEvent } from "@/services/api/cronos/user/events/get-all"

function isDateInPast(dateStr: string): boolean {
  const inputDate = startOfDay(parseISO(dateStr))
  const today = startOfDay(new Date())

  return isBefore(inputDate, today)
}

function msToHour(ms: number): number {
  const hours = ms / (1000 * 60 * 60)
  return Math.round(hours)
}

const getTotalSecondsOfEvents = (events: UserEvent[]): number => {
  const totalSeconds = events.reduce((total, event) => {
    const startDate = new Date(event.startDate)
    const endDate = new Date(event.endDate)
    const eventDurationInSeconds = differenceInSeconds(endDate, startDate)
    return total + eventDurationInSeconds
  }, 0)

  return totalSeconds
}

interface WeeklyStudyData {
  week: number
  barHeight: number
  totalSeconds: number
  formattedDuration: number
  isCurrentWeek: boolean
  isAnOldWeek: boolean
  isOverloaded: boolean
  filtered: {
    barHeight: number
    totalSeconds: number
  }
  past: {
    fixedBackgroundInHours: number // Sum of ALL study hours completed.
    filteredMainInHours: number
    filteredIndicatorInHours: number
  }
}

interface StudyTimeChartProps {
  courses: Course[]
}

export default function StudyTimeChart({ courses }: StudyTimeChartProps) {
  const [currentPage, setCurrentPage] = useState(0)
  const { weeklyObjectives } = useWeeklyObjectivesStore()
  const isLarge = useMediaQuery("(min-width: 1024px)")
  const [currentWeek, setCurrentWeek] = useState(0)

  // Get user events for overload calculation
  const { weekStart, weekEnd } = useMemo(() => {
    const today = new Date()
    const weekStart = startOfWeek(today, { weekStartsOn: 1 })
    const weekEnd = endOfWeek(addWeeks(today, 10), { weekStartsOn: 1 }) // Get events for next 10 weeks
    return { today, weekStart, weekEnd }
  }, [])

  const eventsQuery = useGetCalendar({
    startDate: extractISODate(weekStart),
    endDate: extractISODate(weekEnd),
    category: "study_time", // Only get study time events
  })

  const userEvents = eventsQuery.data?.data.description || []

  // Calculate current week separately to avoid infinite re-renders
  useEffect(() => {
    if (weeklyObjectives.length > 0) {
      const currentWeekIndex = weeklyObjectives.findIndex((week) =>
        isWithinInterval(new Date(), {
          start: toDate(week.startDate),
          end: toDate(week.endDate),
        }),
      )
      if (currentWeekIndex !== -1) {
        setCurrentWeek(currentWeekIndex)
      }
    }
  }, [weeklyObjectives])

  const graphData = useMemo(() => {
    // when filtering by courses, the objectives must be updated, and total seconds
    // of each week must be recalculated accordingly.
    const wk = weeklyObjectives.map((week) => {
      const objectives = week.objectives.filter((objective) =>
        courses.some((c) => c.subjectId === objective.subjectId),
      )

      const totalSeconds = objectives.reduce(
        (sum, obj) => sum + msToSeconds(obj.estimatedRemainingDuration),
        0,
      )

      return {
        ...week,
        startDate: week.startDate,
        endDate: week.endDate,
        objectives,
        totalSeconds,
        totalRealDuration: week.totalRealDuration,
      }
    })

    // create structured weekly objectives data for displaying bars
    const data: WeeklyStudyData[] = wk.map((cv, key) => {
      const isCurrentWeek = isWithinInterval(new Date(), {
        start: toDate(weeklyObjectives[key].startDate),
        end: toDate(weeklyObjectives[key].endDate),
      })

      const isAnOldWeek = !isCurrentWeek && isDateInPast(cv.endDate)

      const totalCompletedHours = weeklyObjectives[key].objectives.reduce(
        (sum, obj) => sum + msToHour(obj.realDuration),
        0,
      )

      const filteredObjectivesBySubject = cv.objectives.filter((objective) =>
        courses.some((c) => c.subjectId === objective.subjectId),
      )

      const filteredMainInHours = filteredObjectivesBySubject.reduce(
        (sum, obj) => sum + msToHour(obj.realDuration),
        0,
      )

      const filteredIndicatorInHours = filteredMainInHours

      // Only calculate overload for current and next week (as per acceptance criteria)
      const currentWeekIndex = wk.findIndex((w) =>
        isWithinInterval(new Date(), {
          start: toDate(w.startDate),
          end: toDate(w.endDate),
        }),
      )

      const isNextWeek = key === currentWeekIndex + 1
      const shouldCalculateOverload = isCurrentWeek || isNextWeek

      let isOverloaded = false

      if (shouldCalculateOverload && !isAnOldWeek) {
        const weekStartDate = new Date(cv.startDate)
        const weekEndDate = new Date(cv.endDate)

        const weekEvents = userEvents.filter((event) => {
          const eventDate = new Date(event.startDate)
          return eventDate >= weekStartDate && eventDate <= weekEndDate
        })

        const availableStudyTimeInSeconds = getTotalSecondsOfEvents(weekEvents)

        // Check if the week is overloaded (remaining work > available study time)
        isOverloaded = cv.totalSeconds > availableStudyTimeInSeconds
      }

      return {
        week: key + 1,
        barHeight: (weeklyObjectives[key].totalSeconds / 3600 / 24) * 100,
        totalSeconds: weeklyObjectives[key].totalSeconds,
        isCurrentWeek,
        isAnOldWeek,
        isOverloaded,
        formattedDuration:
          cv.totalSeconds / 3600 > 0 && cv.totalSeconds / 3600 < 1
            ? 1
            : Math.round(cv.totalSeconds / 3600),
        filtered: {
          barHeight: (cv.totalSeconds / 3600 / 24) * 100,
          totalSeconds: cv.totalSeconds,
        },
        past: {
          fixedBackgroundInHours: totalCompletedHours,
          filteredMainInHours,
          filteredIndicatorInHours,
        },
      }
    })
    return data
  }, [weeklyObjectives, courses, userEvents])

  const ITEMS_PER_PAGE = isLarge ? 12 : 6
  const totalPages = Math.ceil(graphData.length / ITEMS_PER_PAGE)

  const getPageData = () => {
    const start = currentPage * ITEMS_PER_PAGE
    return graphData.slice(start, start + ITEMS_PER_PAGE)
  }

  const weeklyData = getPageData()
  const timeMarkers = [20, 16, 12, 8, 4, 0]

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage((prev) => prev + 1)
    }
  }

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1)
    }
  }

  useEffect(() => {
    if (ITEMS_PER_PAGE >= currentWeek) return
    const page = Math.floor(currentWeek / ITEMS_PER_PAGE)
    setCurrentPage(page)
  }, [currentWeek, isLarge])

  return (
    <Card className="flex w-full flex-col p-4 py-6">
      <CardHeader className="mb-2 p-0">
        <div className="flex flex-col gap-4">
          <div className="mb-2 flex items-center justify-between px-2">
            <div className="inline-flex items-center gap-2">
              <CardTitle className="text-lg font-bold">Tiempo de estudio (hs/sem)</CardTitle>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      asChild
                      variant="ghost"
                      size="icon"
                      className="bg-transparent text-neutral-500 hover:bg-transparent hover:text-neutral-500"
                    >
                      <Info className="!size-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent align="start">
                    <p className="w-[25ch]">
                      Tiempo de estudio estimado por semana para alcanzar tus notas objetivo.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 0}
                className="rounded-full p-1 hover:bg-neutral-100 disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <ChevronLeft size={26} />
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages - 1}
                className="rounded-full p-1 hover:bg-neutral-100 disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <ChevronRight size={26} />
              </button>
            </div>
          </div>
          <div className="mx-auto flex w-full max-w-[90%] items-center justify-center gap-6 px-4">
            {weeklyData.map((t, key) => {
              return t.isCurrentWeek ? (
                <span key={key} className="flex flex-1 justify-center">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <span>
                          <span className="flex h-[35px] w-[35px] items-center justify-center rounded-full bg-primary font-bold text-white">
                            {t.week}
                          </span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent align="center">
                        <p>Semana actual</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </span>
              ) : (
                <span key={key} className="flex flex-1 justify-center">
                  <span className="flex items-center justify-center rounded-full font-normal text-neutral-800">
                    {t.week}
                  </span>
                </span>
              )
            })}
            <div className="flex w-[40px] py-2" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="mx-auto w-full max-w-[90%] p-0">
        <div className="flex gap-4">
          <div className="relative h-60 flex-1">
            <div className="absolute inset-0">
              <div className="absolute flex h-full w-full flex-col justify-between">
                {timeMarkers.map((_, index) => {
                  return (
                    <div
                      key={`h-line-${index}`}
                      className={cn(
                        "h-full w-full border-b border-neutral-200",
                        index === 0 && "border-t",
                      )}
                    />
                  )
                })}
              </div>
              <div className="absolute flex h-full w-full flex-row justify-between">
                {weeklyData.map((index) => {
                  return (
                    <div
                      key={`v-line-${index}`}
                      className={cn(
                        "h-full w-full max-w-[90%] border-x border-dashed border-neutral-200",
                      )}
                    />
                  )
                })}
              </div>
              {/* Individual column overload borders */}
              <div className="absolute flex h-full w-full flex-row justify-between">
                {weeklyData.map((week, index) => {
                  const shouldShowRedBorder =
                    (week.isCurrentWeek || week.week === currentWeek + 2) &&
                    week.isOverloaded &&
                    !week.isAnOldWeek

                  return (
                    <div
                      key={`overload-border-${index}`}
                      className={cn(
                        "z-20 h-full w-full max-w-[90%]",
                        shouldShowRedBorder && "border-t-4 border-t-destructive",
                      )}
                    />
                  )
                })}
              </div>
            </div>
            <div className="absolute inset-0 flex items-end justify-around gap-8 px-4">
              {/* Background gray bar */}
              {weeklyData.map((week, index) => {
                const barHeight = week.isAnOldWeek
                  ? (week.past.fixedBackgroundInHours / 24) * 100
                  : week.barHeight

                const indicator = week.isAnOldWeek
                  ? week.past.fixedBackgroundInHours
                  : week.totalSeconds

                const bgColor = indicator > 0 ? "bg-neutral-200" : "bg-transparent"

                return (
                  <motion.div
                    key={index}
                    className="flex max-h-[100%] min-h-[10%] max-w-7 flex-1 justify-center"
                    initial={{ height: "0%" }}
                    animate={{
                      height: `${barHeight}%`,
                    }}
                    transition={{
                      duration: 0.4,
                      ease: "easeInOut",
                      delay: index * 0.05,
                    }}
                  >
                    <div
                      className={`group relative flex w-full items-end justify-center rounded-sm py-2 ${bgColor}`}
                    />
                  </motion.div>
                )
              })}
            </div>
            <div className="absolute inset-0 flex items-end justify-around gap-8 px-4">
              {/* Green/gray bar. */}
              {weeklyData.map((week, index) => {
                const barHeight = week.isAnOldWeek
                  ? (week.past.filteredMainInHours / 24) * 100
                  : week.filtered.barHeight

                const indicator = week.isAnOldWeek
                  ? week.past.filteredMainInHours
                  : week.filtered.totalSeconds

                const bgColor =
                  indicator > 0
                    ? week.isAnOldWeek
                      ? "bg-neutral-500"
                      : week.isOverloaded
                        ? "bg-gradient-to-b from-primary-100 via-primary-300 to-primary-500 from-[10%] via-[25%] to-[45%]"
                        : "bg-primary-500"
                    : "bg-transparent"

                const canShowTime = week.isAnOldWeek
                  ? week.past.filteredMainInHours > 0
                  : week.filtered.totalSeconds > 0

                const pastTime =
                  week.past.filteredIndicatorInHours <= 1 ? 1 : week.past?.filteredIndicatorInHours

                const timeToShow = week.isAnOldWeek ? pastTime : week.formattedDuration

                return (
                  <motion.div
                    key={index}
                    className="flex max-h-[100%] min-h-[10%] max-w-7 flex-1 justify-center"
                    initial={{ height: "0%" }}
                    animate={{
                      height: `${barHeight}%`,
                    }}
                    transition={{
                      duration: 0.4,
                      ease: "easeInOut",
                      delay: index * 0.05,
                    }}
                  >
                    <div
                      className={cn(
                        "group relative flex w-full items-end justify-center rounded-sm pt-2",
                        bgColor,
                      )}
                    >
                      {canShowTime && (
                        <p className={"text-lg font-bold text-white"}>{timeToShow}</p>
                      )}
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
          <div className="flex w-[40px] flex-col justify-between text-sm text-neutral-500">
            {timeMarkers.map((hours) => (
              <span
                key={hours}
                className="flex h-full w-full items-end justify-center self-center text-center"
              >
                {hours}h
              </span>
            ))}
          </div>
        </div>
      </CardContent>
      <CardFooter className="mt-4 gap-4 p-0">
        <div className="inline-flex items-center gap-2">
          <div className="size-3 rounded bg-primary-500" />
          <p className="pt-1 text-xs leading-[150%] text-muted-foreground">
            Horas de estudio restantes
          </p>
        </div>
        <div className="inline-flex items-center gap-2">
          <div className="size-3 rounded bg-neutral-500" />
          <p className="pt-1 text-xs leading-[150%] text-muted-foreground">
            Horas de estudio realizadas
          </p>
        </div>
      </CardFooter>
    </Card>
  )
}
